import React, { FC } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { ImageDataLike } from 'gatsby-plugin-image';
import { Link } from 'gatsby';
import { Typography } from '../typography/Typography';
import { Image } from '../Image/Image';
import { useBackgroundColor } from '../../utils';
import { Stack } from '../stack/Stack';
import { Tag } from '../tag/Tag';
import { Html } from '../html/Html';
import { ArrowRight } from '../icon';
import token from './token';
// Interface

export interface CardProps {
  image?: ImageDataLike | string;
  category?: string;
  heading?: string;
  description?: string;
  url?: string;
  tags?: string[];
  linkState?: any;
  inversed?: boolean;
  hideLink?: boolean;
  headingSize?: 'small' | 'medium';
  linkLabel?: string;
  arrowRight?: boolean;
  jobType?: string;
  location?: string;
  role?: string;
  salary?: string;
  salaryExtraInfo?: string;
  borderRadius?: 'all' | 'none';
}

interface StyledCardBodyProps {
  inversed?: boolean;
}

interface StyledLinkProps {
  hideLink?: boolean;
  arrowRight?: boolean;
}

// Styles

const StyledCard = styled.div`
  ${({ theme: { borderRadii } }) => css`
    overflow: hidden;
    border-radius: ${borderRadii.medium};
    width: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
    height: 100%;

    .gatsby-image-wrapper {
      width: 100%;
    }

    a {
      position: unset;

      &:before {
        content: '';
        position: absolute;
        display: block;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
      }
    }
  `};
`;

const StyledCardBody = styled.div<StyledCardBodyProps>`
  ${({ theme: { space, colors, media }, inversed }) => css`
    ${useBackgroundColor(inversed ? 'brandOne100' : 'utilityWhite')}
    padding: ${space.fluidMedium};
    flex-grow: 1;
    display: flex;
    flex-direction: column;
  `};
`;

const StyledContent = styled(Stack)`
  ${({ theme: { space, colors, media } }) => css`
    flex-grow: 1;
  `};
`;

const StyledTags = styled.ul`
  ${({ theme: { space, colors, media } }) => css`
    display: flex;
    gap: ${space.xxSmall};
    margin-top: ${space.fluidMedium};
    padding-top: ${space.large};
    border-top: 1px solid ${colors.brandOne80};
  `};
`;

const StyledStack = styled(Stack)`
  ${({ theme: { space, colors, media } }) => css`
    margin-top: ${space.fluidXxSmallToLarge};
    padding-top: ${space.fluidXxSmallToLarge};
    border-top: 1px solid ${colors.brandOne20};
  `};
`;

/*
 * Styled Component styles for both internal and external card links.
 */
const sharedLinkStyles = (
  space: any,
  colors: any,
  hideLink?: boolean,
  arrowRight?: boolean
) => css`
  display: flex;
  gap: ${space.xxSmall};
  align-items: center;

  &:hover,
  &:focus,
  &:active {
    color: ${colors[token.hoverColour]};
  }

  ${hideLink
    ? css`
        opacity: 0;
        height: 1px;
        display: block;
      `
    : css`
        border-top: solid 1px
          ${arrowRight ? colors.brandOne20 : colors.brandOne80};
        padding-top: ${space.fluidXxSmallToLarge};
        margin-top: ${space.fluidXxSmallToLarge};
      `}
`;

/*
 * Using the Gatsby Link component.
 */
const StyledLink = styled(Link, {
  shouldForwardProp: (prop) => !['hideLink', 'arrowRight'].includes(prop),
})<StyledLinkProps>`
  ${({ theme: { space, colors }, hideLink, arrowRight }) =>
    sharedLinkStyles(space, colors, hideLink, arrowRight)};
`;

/*
 * Using a traditional <a> tag.
 */
const StyledLinkA = styled('a', {
  shouldForwardProp: (prop) => !['hideLink', 'arrowRight'].includes(prop),
})<StyledLinkProps>`
  ${({ theme: { space, colors }, hideLink, arrowRight }) =>
    sharedLinkStyles(space, colors, hideLink, arrowRight)};
`;

// JSX

export const Card: FC<CardProps> = ({
  image,
  category,
  heading,
  description,
  linkLabel,
  url,
  tags,
  linkState,
  inversed,
  hideLink,
  headingSize = 'small',
  arrowRight,
  jobType,
  location,
  role: cardRole,
  salary,
  salaryExtraInfo,
  borderRadius,
}) => (
  <StyledCard
    cardRole={cardRole}
    location={location}
    jobType={jobType}
    salary={salary}
  >
    {image && (
      <div>
        <Image src={image} alt="" borderRadius={borderRadius} />
      </div>
    )}
    <StyledCardBody inversed={inversed}>
      <StyledContent gap="small">
        {category && (
          <Typography component="span" variant="caps">
            {category}
          </Typography>
        )}
        {heading && (
          <Typography
            component="h3"
            variant={`heading${
              `${headingSize.charAt(0).toUpperCase()}${headingSize.slice(
                1
              )}` as 'Small' | 'Medium'
            }`}
          >
            {heading}
          </Typography>
        )}
        {description && <Html>{description}</Html>}
      </StyledContent>
      {tags && (
        <StyledTags>
          {tags.map((tag) => (
            <li key={tag}>
              <Tag label={tag} small />
            </li>
          ))}
        </StyledTags>
      )}
      {(location || cardRole || jobType || salary) && (
        <StyledStack gap="fluidXSmall">
          <div>
            {location || cardRole ? (
              <Typography component="p" variant="bodyMedium">
                {location && `${location}`}
                {location && cardRole && ' / '}
                {cardRole && `${cardRole}`}
              </Typography>
            ) : null}
            {jobType && (
              <Typography component="p" variant="bodyMedium">
                {jobType}
              </Typography>
            )}
          </div>
          <div>
            {salary && (
              <Typography component="p" variant="bodyMediumBold">
                {salary}
              </Typography>
            )}
            {salaryExtraInfo && (
              <Typography component="p" variant="bodyMedium">
                {salaryExtraInfo}
              </Typography>
            )}
          </div>
        </StyledStack>
      )}
      {url &&
        (url.startsWith('http') ? ( // External links should not use Gatsby's Link component.
          <StyledLinkA href={url} hideLink={hideLink} arrowRight={arrowRight}>
            {linkLabel || heading}
            {arrowRight && <ArrowRight />}
          </StyledLinkA>
        ) : (
          <StyledLink
            to={url}
            state={linkState}
            hideLink={hideLink}
            arrowRight={arrowRight}
          >
            {linkLabel || heading}
            {arrowRight && <ArrowRight />}
          </StyledLink>
        ))}
    </StyledCardBody>
  </StyledCard>
);

export default Card;
